/**
 * Registers a handler for the catalog's `navigate` action.
 *
 * The catalog (server-side, in `noki-voice-copilot/host_actions.py`)
 * owns the action's name, description, parameter schema, and
 * classification. This module only declares "noki-fe implements
 * `navigate`" and provides the handler that runs when the agent
 * dispatches it.
 *
 * Guard parity with the sidemenu (Aside.jsx:522-540): when the user's
 * CURRENT patient/encounter has a recording in progress (and the SOAP
 * generation isn't ready) OR a Zoom call is ongoing, we do NOT navigate.
 * We dispatch the same Redux actions the click path dispatches —
 * `set_show_popup(true, to)` to spawn the leave-the-encounter popup,
 * or `set_show_zoom_popup(true)` to spawn the Zoom warning — and we
 * return a typed status describing what happened. The agent uses the
 * status to drive the next utterance:
 *
 *   • recording_in_progress → ask the user "save as draft, discard, or
 *     stay?" — then call `save_and_leave_encounter` /
 *     `discard_and_leave_encounter` / `cancel_leave_encounter`. Those
 *     tools are already registered (TranscriptionWrapper mounts
 *     useCopilotDraftLeaveActions, scope `draft_leave_popup`) and they
 *     own the actual save / discard / close-popup wiring, including
 *     the post-save navigation to the path we passed in.
 *   • zoom_call_ongoing → tell the user to end the call first. There
 *     is no voice control for the Zoom popup — its only button is OK
 *     and it doesn't dismiss until the call ends.
 *   • otherwise → navigate immediately.
 *
 * The guards read the CURRENT patient/encounter pair from redux. When
 * the user is on the dashboard (no current patient), the selectors
 * return `recording: false` / `paused: false` and the guard cleanly
 * no-ops, exactly like the sidemenu's click handler.
 */

import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { getCurrentProgressState } from "config/helpers/progressStateSelector";
import { getCurrentRecordingState } from "config/helpers/recordingStateSelector";
import {
  set_show_popup,
  set_show_zoom_popup,
} from "redux/actions/ScribeAcitons";

import { useCopilotAction } from "./actionRegistry";

// The descriptor is now a *claim*: "noki-fe implements `navigate` and
// accepts these argument names". Description / schema / examples live
// in the server catalog so the LLM and the host can never disagree.
const NAVIGATE_DESCRIPTOR = Object.freeze({
  name: "navigate",
  description: "Implemented by noki-fe (react-router-v6).",
  classification: "READ",
  parameters: [
    { name: "to", type: "string", description: "Route path", required: true },
    { name: "replace", type: "boolean", description: "Replace history?", required: false },
  ],
});

export function useCopilotNavigation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const descriptor = useMemo(() => NAVIGATE_DESCRIPTOR, []);

  // Mirror Aside.jsx's selectors exactly so voice and click read the
  // same source of truth. If Aside's logic changes (e.g. a third guard
  // is added), the diff there should be reflected here.
  const patientId = useSelector((s) => s?.patientObj?.patientObj?.id);
  const encounterId = useSelector((s) => s?.encounterObj?.encounter?.id);
  const activeGenerations = useSelector(
    (s) => s?.activeSOAPGeneration?.activeGenerations,
  );
  const { recording, paused } = useSelector((s) =>
    getCurrentRecordingState(s, patientId, encounterId),
  );
  const progressState = useSelector((s) =>
    getCurrentProgressState(s, patientId, encounterId, activeGenerations),
  );
  const isProgressReady = progressState?.isProgressReady;
  const zoomCallOngoing = useSelector(
    (s) => s?.recognizer?.zoomCallOngoing,
  );

  useCopilotAction(descriptor, async (args) => {
    const rawTo = typeof args?.to === "string" ? args.to.trim() : "";
    // eslint-disable-next-line no-console
    console.info("[copilot] navigate.invoke", { raw_to: rawTo, args });

    // Resolve common natural-language route names to the actual app
    // route. The agent often sends short / friendly forms ("/patients",
    // "/dashboard") that don't match noki-fe's nested route table
    // (`/patients/list`, `/home/dashboard`, …). Without this map, those
    // navigate fine syntactically but React Router 404s into the
    // wildcard ERROR_PAGE and the user sees a blank / wrong screen
    // while the FE reports status:ok back to the agent.
    const ROUTE_ALIASES = {
      "/patients": "/patients/list",
      "/patient": "/patients/list",
      "/patient-list": "/patients/list",
      "/patientlist": "/patients/list",
      "/patients-list": "/patients/list",
      "/dashboard": "/home/dashboard",
      "/home": "/home/dashboard",
      "/insights": "/insights/info",
      "/settings": "/settings/me",
    };
    const lookup = ROUTE_ALIASES[rawTo?.toLowerCase()];
    const to = lookup || rawTo;
    if (lookup) {
      // eslint-disable-next-line no-console
      console.info("[copilot] navigate.aliased", { from: rawTo, to: lookup });
    }

    if (!to.startsWith("/")) {
      // Throwing surfaces as host_exception on the server; the agent
      // gets a structured error and can ask the user to clarify.
      throw new Error(
        `'to' must be an in-app route starting with '/'. Got ${JSON.stringify(args?.to)}.`,
      );
    }

    // Guard #1: an encounter recording is in progress and the SOAP
    // generation hasn't completed. We open the leave-the-encounter
    // popup (same as a sidemenu click) instead of dumping the user
    // off the recording screen. The popup carries the destination
    // path so its Save-as-Draft / Discard handlers can finish the
    // navigation after they finish the save/discard.
    if ((recording || paused) && !isProgressReady) {
      dispatch(set_show_popup(true, to));
      return {
        navigated: false,
        blocked_by: "recording_in_progress",
        leave_popup_opened: true,
        target_route: to,
        next_step:
          "The leave-the-encounter popup just opened. Ask the user: 'You have a recording in progress. Save it as a draft, discard it, or stay on the recording?' Then call save_and_leave_encounter (saves + navigates), discard_and_leave_encounter (discards + navigates), or cancel_leave_encounter (closes the popup, stays put).",
      };
    }

    // Guard #2: a Zoom call is ongoing. The Zoom popup is purely
    // informational — its only button closes itself, but it ignores
    // the click while the call is still active. Voice cannot
    // meaningfully end the Zoom call for the user; we just tell them.
    if (zoomCallOngoing) {
      dispatch(set_show_zoom_popup(true));
      return {
        navigated: false,
        blocked_by: "zoom_call_ongoing",
        zoom_popup_opened: true,
        target_route: to,
        next_step:
          "A Zoom call is in progress. Tell the user: 'You're in a Zoom call right now. Please end the call before navigating away — I'll take you there after.' Do NOT call navigate again until the user confirms the call has ended.",
      };
    }

    // Happy path — no guards triggered.
    navigate(to, { replace: Boolean(args?.replace) });
    // eslint-disable-next-line no-console
    console.info("[copilot] navigate.dispatched", { to, replaced: Boolean(args?.replace) });
    return {
      navigated: true,
      route: to,
      // Surface BOTH the resolved route and the original so the agent
      // can recover if it sent a stale form ("/patients") that was
      // aliased to "/patients/list".
      ...(rawTo !== to ? { resolved_from: rawTo } : {}),
      replaced: Boolean(args?.replace),
      next_step: `Navigation dispatched to ${to}. Briefly confirm to the user (e.g. "Here's ${to.split('/').filter(Boolean).join(' / ')}."). If they then say the page didn't load or looks wrong, the route may have been incorrect — ask which page they want.`,
    };
  });
}
